<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: How does this account class roll up, and is any of it sitting on a category
            that has since grown children (the "Egyéb" case)?
  Answer:   the rollup tree - own and rolled-up balance per account, plus the attention-
            toned "Egyéb" row wherever a parent carries stray direct postings.
  Context:  the class/date/currency the tree was built for.
  Controls: the class select and the date, plus the Reports tab strip
            shared by this destination's three views.
-->
<script setup lang="ts">
import { ErrorState } from '@taler/ui'
import { formatLocalDate, systemClock, todayUtc } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  canViewReporting,
  ClassSelect,
  DateStepper,
  ParentRollupTable,
  ReportTabs,
  useParentRollup,
  useParentRollupQueryState,
} from '@/features/reporting'
import { useDisplayCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'

const { t, locale } = useI18n()
const session = useSession()
const today = () => formatLocalDate(todayUtc(systemClock))
const queryState = useParentRollupQueryState(today)
const { displayCurrency } = useDisplayCurrency()

const canView = computed(() => canViewReporting(session.can))

const rollup = useParentRollup({
  ledgerId: () => session.activeLedger.value?.id,
  // This screen has no granularity control of its own - it wants one snapshot, not a
  // trend - so `bucket` is fixed at the widest size, minimizing how many periods
  // `useParentRollup` has to collapse back into that snapshot. See its own comment.
  query: () => ({
    class: queryState.accountClass.value,
    bucket: 'YEAR',
    until: queryState.until.value,
    displayCurrency: displayCurrency.value,
  }),
  locale: () => locale.value as 'en' | 'hu',
  enabled: () => canView.value,
})
</script>

<template>
  <main class="mx-auto flex w-full max-w-[92.5rem] flex-col gap-6 p-[var(--space-page)]">
    <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
      {{ t('reporting.title') }}
    </h1>
    <ReportTabs />

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noLedger') }}
    </p>
    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">{{ t('reporting.noAccess') }}</p>

    <template v-else>
      <div class="flex flex-wrap items-end gap-4">
        <ClassSelect
          :model-value="queryState.accountClass.value"
          @update:model-value="queryState.setAccountClass($event)"
        />
        <label
          class="flex items-center gap-2 text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
        >
          <span class="text-[color:var(--color-text-muted)]">{{ t('reporting.until') }}</span>
          <input
            type="date"
            :max="today()"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-2 py-1 text-[color:var(--color-text)]"
            :value="queryState.until.value"
            @change="queryState.setUntil(($event.target as HTMLInputElement).value)"
          />
        </label>
        <DateStepper
          :model-value="queryState.until.value"
          :max="today()"
          @update:model-value="queryState.setUntil($event)"
        />
      </div>

      <p v-if="rollup.isLoading.value" role="status">{{ t('reporting.loading') }}</p>

      <ErrorState
        v-else-if="rollup.error.value"
        :title="t('reporting.errorTitle')"
        :retry-label="t('reporting.retry')"
        :retrying="rollup.retrying.value"
        @retry="rollup.retry()"
      />

      <ParentRollupTable
        v-else-if="rollup.rows.value"
        :rows="rollup.rows.value"
        :account-class="queryState.accountClass.value"
      />
    </template>
  </main>
</template>
